import { useCallback, useState } from 'react';
import type { ShootScriptRow } from '../../../shared/scriptRows';

/**
 * The Script Shoot View's filter (mock-up C3): every row, only the lines
 * spoken to camera, or only the voiceover. Remembered per script for the
 * tab's lifetime in sessionStorage, keyed by slug, so a
 * reload on set keeps the camera op's choice without it following them to
 * another day's shoot.
 */

export type ScriptFilter = 'all' | 'to_camera' | 'voiceover';

export const SCRIPT_FILTERS: readonly ScriptFilter[] = ['all', 'to_camera', 'voiceover'];

export function scriptFilterKey(slug: string): string {
  return `shoot-filter:${slug}`;
}

export function readStoredScriptFilter(slug: string): ScriptFilter | null {
  try {
    const raw = window.sessionStorage.getItem(scriptFilterKey(slug));
    return raw === 'all' || raw === 'to_camera' || raw === 'voiceover' ? raw : null;
  } catch {
    return null;
  }
}

function writeStoredScriptFilter(slug: string, filter: ScriptFilter): void {
  try {
    window.sessionStorage.setItem(scriptFilterKey(slug), filter);
  } catch {
    // Remembering the filter is a nicety, never a requirement.
  }
}

export function filterCounts(rows: ShootScriptRow[]): Record<ScriptFilter, number> {
  return {
    all: rows.length,
    to_camera: rows.filter((r) => r.audioType === 'to_camera').length,
    voiceover: rows.filter((r) => r.audioType === 'voiceover').length,
  };
}

export function applyScriptFilter(rows: ShootScriptRow[], filter: ScriptFilter): ShootScriptRow[] {
  return filter === 'all' ? rows : rows.filter((r) => r.audioType === filter);
}

/** A filter with nothing under it is hidden, so it can never be the one in use. */
export function effectiveScriptFilter(
  chosen: ScriptFilter | null,
  counts: Record<ScriptFilter, number>,
): ScriptFilter {
  return chosen && counts[chosen] > 0 ? chosen : 'all';
}

export function useScriptFilter(
  slug: string,
  counts: Record<ScriptFilter, number>,
): [ScriptFilter, (filter: ScriptFilter) => void] {
  const [chosen, setChosen] = useState<{ slug: string; filter: ScriptFilter } | null>(null);
  const setFilter = useCallback(
    (filter: ScriptFilter) => {
      setChosen({ slug, filter });
      if (slug) writeStoredScriptFilter(slug, filter);
    },
    [slug],
  );
  const raw = chosen?.slug === slug ? chosen.filter : slug ? readStoredScriptFilter(slug) : null;
  return [effectiveScriptFilter(raw, counts), setFilter];
}

export { shootableRows } from '../../../shared/scriptRows';
